import { PageAbsolutePath, ProductTypes } from '../../consts/Common';
/**
 * Product and quote utility functions.
 *
 * Includes helpers for:
 * - Selecting and filtering product variants (tyre type, claim limit, cover
 *   level, payment method, duration, excess).
 * - Computing instalment pricing and totals across selected products.
 * - Building and filtering product/document trees for display based on
 *   selection state and current page.
 * - Mapping CMS customisations to key benefits and rendering benefit lists.
 * - Detecting invalid product-type combinations.
 */
import {
  CoverLevel,
  KeyBenefitList,
  DocumentTreeItem,
  LinkedProduct,
  ProductType,
  ProductTypeAndDocs,
  ProductVariant,
  QuotePrice,
  QuoteProduct,
  RenewalProductTypeCustomisation,
  SelectedLinkedProduct,
  Totals,
  CoverLevelCustomisation,
  ProductTypeCustomisation,
  KeyBenefitItem
} from './CommonTypes';
import { CommonValidation } from './CommonValidation/commonValidation';
import { InvalidProductTypeCombinations, PaymentMethod } from './Constants';
import { numericSort } from '../../utils/functions';

export const getSelectedProducts = (products: ProductType[]): ProductType[] => {
  const selectedProducts = products.filter(
    (product) => product.isSelected === true
  );

  return selectedProducts;
};

export const getSelectedVariant = (
  variants: ProductVariant[]
): ProductVariant | undefined => {
  const variant = variants.find((v) => v.isSelected === true);

  return variant;
};
export const getFilteredVariants = (
  productVariants: ProductVariant[],
  tyreTypeId?: number,
  claimLimit?: number,
  coverLevelValue?: number,
  paymentMethod?: string,
  duration?: string,
  excess?: number
): ProductVariant[] => {
  const variants = productVariants.filter((variant) => {
    return (
      (!tyreTypeId || variant.tyreTypeId === tyreTypeId) &&
      (!claimLimit || variant.claimLimit === claimLimit) &&
      (!coverLevelValue || variant.coverLevelId === coverLevelValue) &&
      (!paymentMethod || variant.paymentMethod === paymentMethod) &&
      (!duration || variant.duration === duration) &&
      (!excess || variant.excess === excess)
    );
  });

  return variants;
};

export const getUniqueTyreTypes = (
  productVariants: ProductVariant[]
): number[] => {
  const tyreTypes: number[] = [];

  productVariants.forEach((variant) => {
    if (
      variant.tyreTypeId > 0 &&
      !tyreTypes.find((tt) => tt === variant.tyreTypeId)
    ) {
      tyreTypes.push(variant.tyreTypeId);
    }
  });

  tyreTypes.sort(numericSort);

  return tyreTypes;
};

export const getUniqueClaimLimits = (
  productVariants: ProductVariant[],
  tyreTypeId: number | undefined,
  currencySymbol: string
): ClaimLimit[] => {
  const claimLimits: ClaimLimit[] = [];

  const variants = getFilteredVariants(productVariants, tyreTypeId);

  variants.forEach((variant) => {
    const claimLimit = {
      value: variant.claimLimit,
      text: `${currencySymbol}${variant.claimLimit}`
    };

    if (!claimLimits.find((cl) => cl.value === claimLimit.value)) {
      claimLimits.push(claimLimit);
    }
  });

  claimLimits.sort(function (a, b) {
    if (a.value < b.value) {
      return -1;
    }
    if (a.value > b.value) {
      return 1;
    }
    return 0;
  });

  return claimLimits;
};

export const getUniqueCoverLevels = (
  productVariants: ProductVariant[]
): CoverLevel[] => {
  const coverLevels: CoverLevel[] = [];

  productVariants.sort(function (a, b) {
    if (a.claimLimit > 0 && b.claimLimit > 0 && a.claimLimit !== b.claimLimit) {
      return a.claimLimit - b.claimLimit;
    }

    if (a.maxNoOfClaims > b.maxNoOfClaims) {
      return 1;
    } else if (a.maxNoOfClaims < b.maxNoOfClaims) {
      return -1;
    } else if (a.customerFriendlyName > b.customerFriendlyName) {
      return 1;
    } else if (a.customerFriendlyName < b.customerFriendlyName) {
      return -1;
    } else return 0;
  });

  productVariants.forEach((variant) => {
    const coverLevel = {
      value: variant.coverLevelId,
      text: variant.customerFriendlyName
    };

    if (!coverLevels.find((cl) => cl.value === coverLevel.value)) {
      coverLevels.push(coverLevel);
    }
  });

  return coverLevels;
};

export const getUniquePaymentMethods = (
  productVariants: ProductVariant[],
  tyreType: number | undefined,
  claimLimit: number | undefined,
  coverLevel: number | undefined,
  excess: number | undefined
): string[] => {
  let variants = undefined;

  if (tyreType) {
    variants = getFilteredVariants(productVariants, tyreType, claimLimit);
  } else {
    variants = getFilteredVariants(
      productVariants,
      tyreType,
      claimLimit,
      coverLevel,
      undefined,
      undefined,
      excess
    );
  }

  const paymentMethods = variants.map((variant) => variant.paymentMethod);

  const uniquePaymentMethods = [...new Set(paymentMethods)];

  return uniquePaymentMethods;
};

export const getUniqueDurations = (
  productVariants: ProductVariant[],
  tyreTypeId: number | undefined,
  claimLimit: number | undefined,
  coverLevelValue: number | undefined,
  paymentMethod: string | undefined,
  excess: number | undefined
): string[] => {
  const variants = getFilteredVariants(
    productVariants,
    tyreTypeId,
    claimLimit,
    coverLevelValue,
    paymentMethod,
    undefined,
    excess
  );

  const durations = variants.map((variant) => variant.duration);

  const uniqueDurations = [...new Set(durations)];

  uniqueDurations.sort();

  return uniqueDurations;
};

export const excessExists = (excess?: number) => {
  return Number.isInteger(excess);
};

const renewalVariantsExcessFilter = (excess?: number) => (v: ProductVariant) =>
  !excessExists(excess) || excess === v.excess;

export const getUniqueRenewalDurations = (
  productVariants: ProductVariant[],
  { excess }: { excess?: number } = {}
) => {
  let variants = productVariants.filter(
    (v) => v.paymentMethod !== PaymentMethod.Subscription
  );

  if (excess) {
    variants = variants.filter(renewalVariantsExcessFilter(excess));
  }
  return new Set(variants.map((v) => v.duration));
};

export const productHasExcess = (product: ProductType): boolean => {
  return product.productVariants.some((v) => v.excess > 0);
};

export const filterRenewalVariants = (
  variants: ProductVariant[],
  { duration, excess }: { duration?: number; excess?: number }
) => {
  return variants
    .filter(
      (v) =>
        !duration ||
        Number(v.duration) === duration ||
        v.paymentMethod === PaymentMethod.Subscription
    )
    .filter(renewalVariantsExcessFilter(excess));
};

export const getRenewalVariantsData = (
  productVariants: ProductVariant[],
  excess?: number
) => {
  const availableVariants = productVariants.filter(
    renewalVariantsExcessFilter(excess)
  );
  const durations = getUniqueRenewalDurations(availableVariants);

  const uniqueDurations = Array.from(durations).sort();
  const preselectedDuration =
    uniqueDurations.length > 1 ? 0 : Number(uniqueDurations[0]);

  return { uniqueDurations, availableVariants, preselectedDuration };
};

export const getRenewalExcessData = (
  productVariants: ProductVariant[],
  currencySymbol: string
) => {
  const uniqueExcesses = Array.from(
    new Set(productVariants.map((v) => v.excess))
  )
    .sort(numericSort)
    .map((d) => ({
      value: String(d),
      text: `${currencySymbol}${d}`
    }));

  const preselectedExcess =
    uniqueExcesses.length === 1 ? Number(uniqueExcesses[0].value) : null;

  const displayExcessOptions = uniqueExcesses.length > 1;
  return { uniqueExcesses, preselectedExcess, displayExcessOptions };
};

export const getDefaultClaimLimit = (
  productVariants: ProductVariant[],
  tyreTypeId: number | undefined,
  currencySymbol: string
): ClaimLimit | undefined => {
  const updatedClaimLimits = getUniqueClaimLimits(
    productVariants,
    tyreTypeId,
    currencySymbol
  );

  return updatedClaimLimits.length === 1 ? updatedClaimLimits[0] : undefined;
};

export const getDefaultCoverLevel = (
  productVariants: ProductVariant[]
): CoverLevel | undefined => {
  const updatedCoverLevels = getUniqueCoverLevels(productVariants);
  return updatedCoverLevels.length === 1 ? updatedCoverLevels[0] : undefined;
};

export const getDefaultPaymentMethod = (
  productVariants: ProductVariant[],
  tyreTypeId: number | undefined,
  claimLimit: number | undefined,
  coverLevelValue: number | undefined,
  excess?: number
): string => {
  const updatedPaymentMethods = getUniquePaymentMethods(
    productVariants,
    tyreTypeId,
    claimLimit,
    coverLevelValue,
    excess
  );
  return updatedPaymentMethods.length === 1 ? updatedPaymentMethods[0] : '';
};

export const getDefaultDuration = (
  productVariants: ProductVariant[],
  tyreTypeId: number | undefined,
  claimLimit: number | undefined,
  coverLevelValue: number | undefined,
  paymentMethod: string | undefined
): string => {
  const updatedDurations = getUniqueDurations(
    productVariants,
    tyreTypeId,
    claimLimit,
    coverLevelValue,
    paymentMethod,
    undefined
  );

  return updatedDurations.length === 1 ? updatedDurations[0] : '';
};

export const getDefaultVariant = (
  productVariants: ProductVariant[],
  tyreTypeId: number | undefined,
  claimLimit: number | undefined,
  coverLevelValue: number | undefined,
  paymentMethod: string | undefined,
  duration: string | undefined,
  excess: number | undefined
): ProductVariant | undefined => {
  if (tyreTypeId) {
    return productVariants.find(
      (variant) =>
        variant.tyreTypeId === tyreTypeId &&
        claimLimit &&
        variant.claimLimit === claimLimit &&
        paymentMethod &&
        variant.paymentMethod === paymentMethod &&
        duration &&
        variant.duration === duration &&
        (!excess || variant.excess == excess)
    );
  }

  return productVariants.find(
    (variant) =>
      coverLevelValue &&
      variant.coverLevelId === coverLevelValue &&
      paymentMethod &&
      variant.paymentMethod === paymentMethod &&
      duration &&
      variant.duration === duration &&
      (!excess || variant.excess == excess)
  );
};

export const getNoOfInstallments = (products: ProductType[]): number => {
  if (!products || products.length === 0) return 0;
  const noOfInstallments = Math.max(
    ...products
      .map((p) => p.productVariants)
      .reduce((a, b) => a.concat(b))
      .map((v) => v.noOfInstallments),
    0
  );

  return noOfInstallments;
};

export const calculateTotals = (
  products: ProductType[],
  noOfInstallments: number
): Totals => {
  const selectedProducts = getSelectedProducts(products);

  const totals = (): Totals => {
    const calculatedTotals = {
      noOfInstallments: noOfInstallments,
      selectedProductsCount: 0,
      selectedFirstMonthlyPrice: 0,
      selectedSubsequentMonthlyPrice: 0,
      selectedSinglePrice: 0,
      selectedSubscriptionPrice: 0,
      selectedTotalPrice: 0
    };

    if (selectedProducts.length === 0) {
      return calculatedTotals;
    }

    selectedProducts.forEach((product) => {
      const selectedVariant = getSelectedVariant(product.productVariants);

      if (selectedVariant) {
        const installment = getVariantInstalmentTotalPrice(
          product.productId,
          selectedVariant,
          noOfInstallments,
          product.selectedLinkedProducts ?? [],
          product.isVariablePrice
        );

        switch (selectedVariant.paymentMethod) {
          case PaymentMethod.Monthly:
          case PaymentMethod.PaymentAssist:
            calculatedTotals.selectedFirstMonthlyPrice +=
              installment.firstInstallmentPrice;
            calculatedTotals.selectedSubsequentMonthlyPrice +=
              installment.subsequentInstallmentPrice;
            break;
          case PaymentMethod.Single:
            calculatedTotals.selectedSinglePrice +=
              installment.firstInstallmentPrice;
            break;
          case PaymentMethod.Subscription:
            calculatedTotals.selectedSubscriptionPrice +=
              installment.firstInstallmentPrice;
            break;
          default:
        }
      }
    });

    calculatedTotals.noOfInstallments = noOfInstallments;

    calculatedTotals.selectedProductsCount = selectedProducts.length;

    calculatedTotals.selectedTotalPrice =
      calculatedTotals.selectedFirstMonthlyPrice +
      calculatedTotals.selectedSinglePrice +
      calculatedTotals.selectedSubsequentMonthlyPrice *
        (calculatedTotals.noOfInstallments - 1);

    return calculatedTotals;
  };

  return totals();
};

export const hasPaymentSelected = (
  products: ProductType[],
  paymentMethod: string
): boolean => {
  const selectedProducts = getSelectedProducts(products);

  const hasPayment = selectedProducts
    .map((p) => p.productVariants)
    .reduce((a, b) => a.concat(b), [])
    .some((v) => v.isSelected === true && v.paymentMethod === paymentMethod);

  return hasPayment;
};

export const hasPaymentType = (
  products: ProductType[],
  paymentMethod: string
): boolean => {
  const hasPayment = products
    .map((p) => p.productVariants)
    .reduce((a, b) => a.concat(b), [])
    .some((v) => v.paymentMethod === paymentMethod);

  return hasPayment;
};

export const validateVariablePrice = (
  selectedVariant: ProductVariant | undefined
): string => {
  if (!selectedVariant) return '';
  if (selectedVariant.price === undefined || selectedVariant.price === null)
    return '';
  if (
    selectedVariant.price < selectedVariant.minimumPrice ||
    selectedVariant.price > selectedVariant.maximumPrice
  )
    return 'Price is invalid';

  return '';
};

export const getLinkedProductNames = (
  quoteCardId: number,
  linkedProducts: LinkedProduct[],
  selectedLinkedProducts: SelectedLinkedProduct[]
): string => {
  const selectedLinkedQuotes = linkedProducts?.filter(
    (lp) =>
      lp.isMandatory === false &&
      selectedLinkedProducts &&
      selectedLinkedProducts.some(
        (slp) =>
          slp.quoteCardId === quoteCardId &&
          slp.linkedProductTypeId === lp.typeId
      )
  );

  if (!selectedLinkedQuotes || selectedLinkedQuotes.length === 0) {
    return '';
  }

  const uniqueLinkedQuoteNames = [
    ...new Set(selectedLinkedQuotes.map((lq) => lq.name.toLowerCase()))
  ];

  const result = uniqueLinkedQuoteNames.join(' & ');

  return result;
};

const convertToList = (value: string) => {
  value = value.replace(/ /g, '');

  return value.split(',');
};

const getBenefitListsFromCustomisation = (
  customisations: RenewalProductTypeCustomisation[],
  productTypeId: number,
  warrantyCoreId?: number,
  productVariants?: ProductVariant[]
): KeyBenefitList[] | undefined => {
  const productTypeCustomisations = customisations.filter(
    (p) => p.productTypeId === productTypeId
  );

  let coverLevelCustomisations: CoverLevelCustomisation[] = [];

  if (warrantyCoreId && warrantyCoreId !== 0) {
    coverLevelCustomisations = productTypeCustomisations
      .map((p) =>
        p.coverLevels.filter((c) =>
          convertToList(c.warrantyCoreId).some(
            (l) => l === warrantyCoreId.toString()
          )
        )
      )
      .reduce((a, b) => a.concat(b), []);
  }

  if (productVariants) {
    coverLevelCustomisations = productTypeCustomisations
      .map((p) =>
        p.coverLevels.filter((c) =>
          convertToList(c.warrantyCoreId).some((l) =>
            productVariants.find((pv) => pv.productId.toString() === l)
          )
        )
      )
      .reduce((a, b) => a.concat(b), []);
  }

  const keyBenefitsLists = coverLevelCustomisations
    ?.map((c) => c.keyBenefitLists)
    .reduce((a, b) => a.concat(b), []);

  const validKeyBenefitsLists = keyBenefitsLists.filter(
    (b) => b.keyBenefits.length > 0
  );

  return validKeyBenefitsLists;
};

const concatBenefitsLists = (
  mainList: KeyBenefitList[],
  appendList: KeyBenefitList[] | undefined
): KeyBenefitList[] => {
  if (appendList && appendList.length > 0) {
    appendList.forEach((pbl) => {
      if (!mainList.find((kbl) => kbl.listId === pbl.listId)) {
        mainList = [...mainList, ...appendList];
      }
    });
  }

  return mainList;
};

export const getCustomerBenefits = (
  customisations: ProductTypeCustomisation[],
  product: ProductType,
  selectedLinkedProducts: SelectedLinkedProduct[] | undefined
): KeyBenefitList[] => {
  const keyBenefitsLists: KeyBenefitList[] = [];

  customisations.forEach((pc) => {
    const { id: listId } = getProductIdMatchIfExists(product, pc);
    if (listId) {
      const productKeyBenefitsItems: KeyBenefitList = {
        title: pc.name,
        listId,
        keyBenefits: pc.seeMoreItems
      };
      keyBenefitsLists.push(productKeyBenefitsItems);
    }
  });

  const linkedProductsKeyBenefitLists = getCustomerLinkedProductBenefits(
    customisations,
    product,
    selectedLinkedProducts
  );

  keyBenefitsLists.push(...linkedProductsKeyBenefitLists);

  return keyBenefitsLists;
};

const getCustomerLinkedProductBenefits = (
  customisations: ProductTypeCustomisation[],
  product: ProductType,
  selectedLinkedProducts: SelectedLinkedProduct[] | undefined
): KeyBenefitList[] => {
  const keyBenefitsLists: KeyBenefitList[] = [];

  const linkedProducts = product.productVariants
    .map((variant) => variant.linkedProducts)
    .reduce((a, b) => a.concat(b), [] as LinkedProduct[]);

  const mandatoryLinkedProducts = linkedProducts
    .filter((lp) => lp.isMandatory)
    .sort(function (a, b) {
      return a.warrantyTierId - b.warrantyTierId;
    });

  mandatoryLinkedProducts.forEach((mlp) => {
    const customisation = customisations.find(
      (pc) => mlp.typeId === pc.productType
    );
    if (customisation) {
      const productKeyBenefitsItems: KeyBenefitList = {
        title: customisation.name,
        listId: customisation.productType,
        keyBenefits: customisation.seeMoreItems
      };
      if (
        !keyBenefitsLists.some(
          (kbl) => kbl.listId === productKeyBenefitsItems.listId
        )
      ) {
        keyBenefitsLists.push(productKeyBenefitsItems);
      }
    }
  });

  const selectedOptionalLinkedProducts = linkedProducts.filter(
    (lp) =>
      lp.isMandatory === false &&
      selectedLinkedProducts &&
      selectedLinkedProducts.find(
        (slp) => slp.linkedProductTypeId === lp.typeId
      )
  );

  selectedOptionalLinkedProducts.forEach((slp) => {
    const customisation = customisations.find(
      (pc) => slp.typeId === pc.productType
    );
    if (customisation) {
      const productKeyBenefitsItems: KeyBenefitList = {
        title: customisation.name,
        listId: customisation.productType,
        keyBenefits: customisation.seeMoreItems
      };

      if (
        !keyBenefitsLists.some(
          (kbl) => kbl.listId === productKeyBenefitsItems.listId
        )
      ) {
        keyBenefitsLists.push(productKeyBenefitsItems);
      }
    }
  });

  return keyBenefitsLists;
};

export const getRenewalBenefits = (
  customisations: RenewalProductTypeCustomisation[],
  quote: ProductType,
  selectedLinkedProducts: SelectedLinkedProduct[] | undefined
): KeyBenefitList[] => {
  let keyBenefitsLists: KeyBenefitList[] = [];

  const productKeyBenefitsLists = getBenefitListsFromCustomisation(
    customisations,
    quote.productTypeId,
    0,
    quote.productVariants
  );

  keyBenefitsLists = concatBenefitsLists(
    keyBenefitsLists,
    productKeyBenefitsLists
  );

  const linkedProducts = quote.productVariants
    .map((variant) => variant.linkedProducts)
    .reduce((a, b) => a.concat(b), [] as LinkedProduct[]);

  const mandatoryLinkedProducts = linkedProducts
    .filter((lp) => lp.isMandatory)
    .sort(function (a, b) {
      return a.warrantyTierId - b.warrantyTierId;
    });

  mandatoryLinkedProducts.forEach((mlp) => {
    const mandatoryKeyBenefitsLists = getBenefitListsFromCustomisation(
      customisations,
      mlp.typeId,
      mlp.warrantyCoreId
    );

    keyBenefitsLists = concatBenefitsLists(
      keyBenefitsLists,
      mandatoryKeyBenefitsLists
    );
  });

  const selectedOptionalLinkedProducts = linkedProducts.filter(
    (lp) =>
      lp.isMandatory === false &&
      selectedLinkedProducts &&
      selectedLinkedProducts.find(
        (slp) =>
          slp.quoteCardId === quote.productId &&
          slp.linkedProductTypeId === lp.typeId
      )
  );

  selectedOptionalLinkedProducts.forEach((slp) => {
    const optionalKeyBenefitsLists = getBenefitListsFromCustomisation(
      customisations,
      slp.typeId,
      slp.warrantyCoreId
    );

    keyBenefitsLists = concatBenefitsLists(
      keyBenefitsLists,
      optionalKeyBenefitsLists
    );
  });

  return keyBenefitsLists;
};

export const renderKeyBenefits = (showMoreItems?: KeyBenefitList[]) => {
  const renderKeyBenefit = (productBenefits: KeyBenefitList) => {
    const benefitsList = productBenefits.keyBenefits;

    return (
      <>
        <p className='bold m1-bottom'>{`${productBenefits.title}:`}</p>
        <div className='m1-bottom m1-left'>
          {benefitsList.map((benefit: KeyBenefitItem, i) => (
            <li className='m05-left' key={`benefit-${i}`}>
              {benefit.text}
            </li>
          ))}
        </div>
      </>
    );
  };

  return showMoreItems?.map((item: KeyBenefitList, i) => (
    <div key={`more-items-${i}`}>{renderKeyBenefit(item)}</div>
  ));
};

const getDocuments = (
  prodDocs: ProductTypeAndDocs[],
  documents: DocumentTreeItem[],
  show: boolean,
  filterBySelectedVariants = false
): DocumentTreeItem[] => {
  if (documents && documents.length > 0) {
    const level = documents[0].level;
    const newDocuments: DocumentTreeItem[] = [...documents];

    switch (level) {
      case 1: {
        newDocuments.forEach((d) => {
          d.show = show;
          getDocuments(
            prodDocs,
            d.children || [],
            d.show,
            filterBySelectedVariants
          );
        });
        break;
      }
      case 2: {
        const productTypeIds = prodDocs.map((p) => p.productTypeId.toString());
        const productTypeCodes = prodDocs.map((p) => p.productTypeCode);

        newDocuments.forEach((d) => {
          if (show === false) {
            d.show = false;
            getDocuments(
              prodDocs,
              d.children || [],
              d.show,
              filterBySelectedVariants
            );
          } else {
            const foundMatch =
              productTypeIds.includes(d.typeId!) ||
              productTypeCodes.includes(d.typeId!);

            d.show = foundMatch ? true : false;
            getDocuments(
              prodDocs,
              d.children || [],
              d.show,
              filterBySelectedVariants
            );
          }
        });
        break;
      }
      case 3: {
        let productDocuments = prodDocs.flatMap((p) => p.productDocuments);

        if (filterBySelectedVariants) {
          const productIds = prodDocs.flatMap((p) => p.selectedProductIds);

          productDocuments = productDocuments.filter(
            (d) => productIds.indexOf(d.productId) >= 0
          );
        }

        const productDocumentCodes = productDocuments.map((d) =>
          d.code.toString()
        );

        newDocuments.forEach((d) => {
          if (show === false) {
            d.show = false;
            getDocuments(
              prodDocs,
              d.children || [],
              d.show,
              filterBySelectedVariants
            );
          } else {
            const foundDocumentCode = productDocumentCodes.find(
              (code) => code === d.typeId!
            );
            d.show = foundDocumentCode ? true : false;
            getDocuments(
              prodDocs,
              d.children || [],
              d.show,
              filterBySelectedVariants
            );
          }
        });
      }
    }

    return newDocuments;
  }

  return documents;
};

export const filterProductDocuments = (
  products: ProductType[],
  gapIntroductionDate: string | undefined,
  documents: DocumentTreeItem[],
  currentPath: string
): DocumentTreeItem[] | undefined => {
  if (documents && documents.length > 0) {
    const showGap = isGapValid(products, gapIntroductionDate);

    let validProducts = products.filter((p) => {
      if (showGap === true || p.productTypeId !== ProductTypes.Gap) {
        return p;
      }
    });

    const selectedOnly = getSelectedOnly(currentPath);
    if (selectedOnly) {
      validProducts = validProducts.filter((p) => p.isSelected === true);
    }

    const prodDocs: ProductTypeAndDocs[] = validProducts
      .filter((vp) => !vp.isPurchased)
      .map((vp) => ({
        productTypeId: vp.productTypeId,
        productTypeCode: vp.productTypeCode,
        productSubTypeId: vp.productSubTypeId,
        productDocuments: vp.productDocuments,
        selectedProductIds: vp.productVariants
          .filter((v) => v.isSelected)
          .map((v) => v.productId)
      }));

    if (!validProducts) {
      return [];
    }

    validProducts.forEach((vp) => {
      vp.productVariants.forEach((v) => {
        v.linkedProducts?.forEach((lp) => {
          if (selectedOnly && lp.isMandatory === false) {
            if (
              vp.selectedLinkedProducts
                ?.map((slp) => slp.linkedProductTypeId)
                .find((typeId) => typeId === lp.typeId)
            ) {
              prodDocs.push({
                productTypeId: lp.typeId,
                productTypeCode: lp.typeId.toString(),
                productSubTypeId: 0,
                productDocuments: lp.productDocuments,
                selectedProductIds: v.isSelected ? [v.productId] : []
              });
            }
          } else {
            prodDocs.push({
              productTypeId: lp.typeId,
              productTypeCode: lp.typeId.toString(),
              productSubTypeId: 0,
              productDocuments: lp.productDocuments,
              selectedProductIds: v.isSelected ? [v.productId] : []
            });
          }
        });
      });
    });

    const selectedVariantsOnly = getSelectedVariantsOnly(currentPath);

    const newDocuments = getDocuments(
      prodDocs,
      documents,
      true,
      selectedVariantsOnly
    );

    newDocuments.forEach((d) => {
      d.children?.forEach((c) => {
        const hasVisibleChildren = c.children
          ? c.children?.some((c2) => c2.show)
          : false;
        // Only hide if it has children but none are visible
        if (c.children && !hasVisibleChildren) {
          c.show = false;
        }
      });
    });
    return newDocuments;
  } else {
    return undefined;
  }
};

const isGapValid = (
  products: ProductType[],
  gapIntroductionDate?: string
): boolean => {
  let gapIsValid = false;

  const gapProduct = products.find((p) => p.productTypeId === ProductTypes.Gap);

  if (gapProduct) {
    gapIsValid = isValidProduct(gapProduct, gapIntroductionDate);
  }

  return gapIsValid;
};

const isValidProduct = (
  product: ProductType,
  vehicleGapIntroductionDate?: string
) => {
  const isDateNull = CommonValidation.isDateNull(
    vehicleGapIntroductionDate,
    true
  );

  const jsonGapDate = CommonValidation.getJsonDate(vehicleGapIntroductionDate);
  const today = new Date().setHours(0, 0, 0, 0);

  let gapDate = new Date().setHours(0, 0, 0, 0);

  if (jsonGapDate) {
    gapDate = new Date(jsonGapDate).setHours(0, 0, 0, 0);
  }

  const isInvalid =
    product.productTypeId === ProductTypes.Gap &&
    (isDateNull || gapDate === today);

  return !isInvalid;
};

const getSelectedVariantsOnly = (currentPath: string): boolean => {
  switch (currentPath) {
    case PageAbsolutePath.PersonalDetails:
    case PageAbsolutePath.Summary:
    case PageAbsolutePath.DirectDebit:
    case PageAbsolutePath.SinglePayment:
    case PageAbsolutePath.Confirmation:
    case PageAbsolutePath.Customer.PersonalDetails:
    case PageAbsolutePath.Customer.Summary:
    case PageAbsolutePath.Customer.DirectDebit:
    case PageAbsolutePath.Customer.SinglePayment:
    case PageAbsolutePath.Customer.Confirmation:
      return true;
    default:
      return false;
  }
};

const getSelectedOnly = (currentPath: string): boolean => {
  switch (currentPath) {
    case PageAbsolutePath.Products:
    case PageAbsolutePath.Renewals.Quote:
    case PageAbsolutePath.Customer.Products:
    case PageAbsolutePath.Customer.VehicleDetails:
      return false;
    default:
      return true;
  }
};

export const checkAddProductPlanConflict = (
  product: string,
  products: ProductType[]
) => {
  if (products.some((p) => p.isSelected && p.productTypeCode === product)) {
    return null; // removing from plan
  }

  const productsMap = new Map(products.map((p) => [p.productTypeCode, p]));
  const selectedProducts = new Set(
    products.filter((p) => p.isSelected).map((p) => p.productTypeCode)
  );

  for (const [combined, singles] of Object.entries(
    InvalidProductTypeCombinations
  )) {
    if (selectedProducts.has(combined) && singles.includes(product)) {
      return {
        combined: productsMap.get(combined)!,
        single: productsMap.get(product)!
      };
    }

    if (product === combined && singles.some((s) => selectedProducts.has(s))) {
      const conflictSingle = singles.find((s) => selectedProducts.has(s))!;
      return {
        combined: productsMap.get(combined)!,
        single: productsMap.get(conflictSingle)!
      };
    }
  }

  return null;
};

export const getInvalidProductTypeCombinations = (
  products: ProductType[]
): [string, string][] => {
  const invalidCombinations: [string, string][] = [];
  const productTypeCodes = products
    .filter((p) => p.isSelected)
    .map((product) => product.productTypeCode);

  // Check if all single product types that are part of a combined one are selected
  Object.entries(InvalidProductTypeCombinations).forEach(
    ([combinedType, singleTypes]) => {
      const combinedTypeExists = products.some(
        (product) => product.productTypeCode === combinedType
      );

      const allSingleTypesSelected = singleTypes.every((singleType) =>
        productTypeCodes.includes(singleType)
      );

      if (combinedTypeExists && allSingleTypesSelected) {
        singleTypes.forEach((singleType) => {
          invalidCombinations.push([combinedType, singleType]);
        });
      }
    }
  );

  return invalidCombinations;
};

export const getQuotedPrice = (
  product: ProductType,
  quotes: QuoteProduct[]
): QuotePrice | undefined => {
  const quotedProduct = quotes.find(
    (quote) =>
      quote.productTypeId === product.productTypeId &&
      (quote.productSubTypeId === null ||
        quote.productSubTypeId === 0 ||
        quote.productSubTypeId === product.productSubTypeId)
  );

  if (!quotedProduct) return undefined;

  const quotedVariant = product.productVariants.find(
    (pv) => pv.variantId === quotedProduct.variantId
  );

  if (!quotedVariant) return undefined;

  const noOfInstallments = getNoOfInstallments([product]);

  const installmentPrice = getInstalmentPrice(
    quotedVariant.price ?? 0,
    noOfInstallments,
    quotedVariant.paymentMethod
  );

  const variantPrice: QuotePrice = {
    variantId: quotedProduct.variantId,
    monthlyPrice: installmentPrice.firstInstallmentPrice,
    totalPrice: quotedVariant.price ?? 0,
    noOfInstallments: noOfInstallments,
    paymentMethod: quotedVariant.paymentMethod
  };

  return variantPrice;
};

export type InstallmentPrice = {
  firstInstallmentPrice: number;
  subsequentInstallmentPrice: number;
  totalNumberOfInstallments: number;
  totalMonthlyAmount: number;
};

export type ClaimLimit = {
  value: number;
  text: string;
};

export const getInstalmentPrice = (
  price: number,
  noOfInstallments: number,
  paymentMethod: string
): InstallmentPrice => {
  let installmentPrice: InstallmentPrice = {
    firstInstallmentPrice: 0,
    subsequentInstallmentPrice: 0,
    totalNumberOfInstallments: 0,
    totalMonthlyAmount: 0
  };

  if (!price) {
    return installmentPrice;
  }

  if (
    paymentMethod === PaymentMethod.Subscription ||
    paymentMethod === PaymentMethod.Single
  ) {
    installmentPrice.firstInstallmentPrice = price;

    return installmentPrice;
  }

  if (noOfInstallments !== 0) {
    const remainder = (Math.round(price * 100) % noOfInstallments) / 100;
    const installment = (price - remainder) / noOfInstallments;

    installmentPrice = {
      firstInstallmentPrice: installment + remainder,
      subsequentInstallmentPrice: installment,
      totalNumberOfInstallments: noOfInstallments,
      totalMonthlyAmount: price
    };
  }

  return installmentPrice;
};

export const getVariantInstalmentTotalPrice = (
  productOrQuoteCardId: number,
  variant: ProductVariant | undefined,
  noOfInstallments: number,
  selectedLinkedProducts: SelectedLinkedProduct[],
  isVariablePrice: boolean
): InstallmentPrice => {
  let totalPrice = variant?.price ?? 0;

  if (variant && !isVariablePrice) {
    const linkedInstallmentPrice = getLinkedProductTotalPrice(
      variant.linkedProducts,
      selectedLinkedProducts,
      productOrQuoteCardId
    );
    totalPrice += linkedInstallmentPrice;
  }
  const installmentPrice = getInstalmentPrice(
    totalPrice ?? 0,
    noOfInstallments,
    variant?.paymentMethod ?? ''
  );

  return installmentPrice;
};

const getLinkedProductTotalPrice = (
  linkedProducts: LinkedProduct[],
  selectedLinkedProducts: SelectedLinkedProduct[],
  quoteCardId: number
): number => {
  let totalPrice = 0;

  if (linkedProducts) {
    linkedProducts.forEach((lp) => {
      const isSelected = selectedLinkedProducts.find(
        (slp) =>
          slp.quoteCardId === quoteCardId &&
          slp.linkedProductTypeId === lp.typeId
      );

      if (isSelected || lp.isMandatory) {
        totalPrice += lp.price;
      }
    });
  }

  return totalPrice;
};

export const getProductInstalmentTotalPriceByPaymentMethod = (
  quote: ProductType,
  paymentMethod: string,
  noOfInstallments: number,
  selectedLinkedProducts: SelectedLinkedProduct[],
  selectedCoverDuration?: number,
  selectedExcess?: number
): InstallmentPrice => {
  const variant = quote.productVariants
    .filter(renewalVariantsExcessFilter(selectedExcess))
    .find(
      (p) =>
        p.paymentMethod === paymentMethod &&
        (selectedCoverDuration !== undefined &&
        selectedCoverDuration !== 0 &&
        hasMultipleDurations(quote.productVariants)
          ? Number(p.duration) === selectedCoverDuration
          : true)
    );

  const installmentPrice = getVariantInstalmentTotalPrice(
    quote.productId,
    variant,
    noOfInstallments,
    selectedLinkedProducts,
    quote.isVariablePrice
  );

  return installmentPrice;
};

export const getVariantTotalPrice = (
  isVariablePricing: boolean,
  variant: ProductVariant | undefined,
  selectedLinkedProducts: SelectedLinkedProduct[] = []
): number | undefined => {
  //used in the product selection component that needs the single total price rather than the instalment breakdown
  if (variant === undefined || variant.price === undefined) {
    return undefined;
  }

  const variantPrice = variant.price ?? 0;

  if (isVariablePricing) {
    //the price is the total price including linked products already
    return variantPrice;
  }

  if (!variant.linkedProducts) {
    return variantPrice;
  }

  let linkedPrice = 0;

  variant.linkedProducts.forEach((lp) => {
    const isSelected = selectedLinkedProducts.find(
      (slp) =>
        slp.quoteCardId === variant.productId &&
        slp.linkedProductTypeId === lp.typeId
    );

    if (isSelected || lp.isMandatory) {
      linkedPrice += lp.price;
    }
  });

  return variantPrice + linkedPrice;
};

export const hasMultipleDurations = (
  productVariants: ProductVariant[]
): boolean => {
  if (!productVariants || productVariants.length === 0) {
    return false;
  }
  const durations = productVariants
    .filter((v) => v.paymentMethod !== PaymentMethod.Subscription)
    .map((v) => v.duration);
  const uniqueDurations = new Set(durations); // TODO: use getUniqueRenewalDurations
  return uniqueDurations.size > 1;
};

export const getUniqueExcesses = (
  productVariants: ProductVariant[],
  tyreTypeId: number | undefined,
  claimLimit: number | undefined,
  coverLevelValue: number | undefined
): number[] => {
  const variants = getFilteredVariants(
    productVariants,
    tyreTypeId,
    claimLimit,
    coverLevelValue
  );

  const excesses = variants.map((variant) => variant.excess);
  const uniqueExcesses = [...new Set(excesses)];

  uniqueExcesses.sort((a, b) => a - b);

  return uniqueExcesses;
};

export const getExcessData = (product: {
  productVariants: ProductVariant[];
  tyreTypeId: number | undefined;
  claimLimit: number | undefined;
  coverLevel: number | undefined;
}) => {
  const uniqueExcesses = getUniqueExcesses(
    product.productVariants,
    product.tyreTypeId,
    product.claimLimit,
    product.coverLevel
  );

  const displayExcessOptions = uniqueExcesses.length > 1;
  const preselectedExcess =
    uniqueExcesses.length === 1 ? uniqueExcesses[0] : undefined;

  return {
    displayExcessOptions,
    preselectedExcess
  };
};

export const getProductIdMatchIfExists = (
  product: ProductType,
  { productType, productSubType }: ProductTypeCustomisation
) => {
  const hasSubtype = !!product.productSubTypeId;

  if (hasSubtype && productSubType === product.productSubTypeId) {
    return { id: product.productSubTypeId, type: 'productSubType' };
  }

  if (!hasSubtype && productType === product.productTypeId) {
    return { id: product.productTypeId, type: 'productType' };
  }

  return {};
};
